import {
  EXPORT_TYPE_NAMES,
  ExportJobTypes,
  ExportTransportTypes,
  ReportNames,
} from 'constants/exportTypes';
import { ExportType } from 'constants/exportTypes';

import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'config/requests';
import { useReportExport } from 'hooks/queries/useReportExport';
import { EXPORT_FIELDS_ADJUSTMENTS_REPORT } from 'pages/OrderAdjustmentsReport/constants/exportFields';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { FilterPayload, useNotify } from 'react-admin';
import { Typography } from 'shared/mui/Typography';
import { ExportEditButton } from 'shared/react-admin/Export/ExportEditButton/ExportEditButton';
import { ReportExportDialog } from 'shared/widgets/ReportExportDialog/ReportExportDialog';

type Props = {
  exportType: ExportType;
  closeNotifyDialog: VoidFunction;
  open: boolean;
  isLoadingData: boolean;
  listFilters: FilterPayload;
};

export const OrderAdjustmentsReportExportDialog: React.FC<Props> = ({
  exportType,
  closeNotifyDialog,
  listFilters,
  open,
  isLoadingData,
}) => {
  const translate = useTypedTranslate();
  const notify = useNotify();
  const [isSendReportOnEmail, setIsSendReportOnEmail] = useState(false);
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: getUserInfo });

  const { mutate: exportMutate, isLoading: isExportLoading } = useReportExport({
    filters: {
      ...listFilters,
      exportType: exportType,
      jobType: ExportJobTypes.Adjustments,
      ...(isSendReportOnEmail && { notificationTransport: ExportTransportTypes.Email }),
    },
    preferenceKey: ReportNames.Order_adjustment_report,
    onSuccess: () => {
      notify(
        translate('app.orderAdjustmentsReport.notify.exportStarted', {
          emailPart: isSendReportOnEmail
            ? translate('app.orderAdjustmentsReport.notify.sentToEmail')
            : '',
        }),
        { type: 'info' },
      );
      closeNotifyDialog();
    },
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  return (
    <ReportExportDialog
      exportType={EXPORT_TYPE_NAMES[exportType]}
      isLoading={isLoadingData || isExportLoading}
      onChange={() => setIsSendReportOnEmail((prev) => !prev)}
      onClose={closeNotifyDialog}
      onSubmit={exportMutate}
      open={open}
      sendOnEmail={isSendReportOnEmail}
      title={
        <>
          <Typography component="span" sx={{ verticalAlign: 'middle' }} variant="inherit">
            {translate('app.orderAdjustmentsReport.dialog.exportTitle')}
          </Typography>
          <ExportEditButton
            fields={EXPORT_FIELDS_ADJUSTMENTS_REPORT}
            preferenceKey={ReportNames.Order_adjustment_report}
          />
        </>
      }
      userEmail={user?.corporateEmail || user?.email}
    />
  );
};
